import { useEffect, useContext } from 'react';
import { EngineCorrectionContext } from '@/providers/EngineCorrectionProvider';
import { Processing } from '@/api/entities/processing';
import { TResultImageCoordinatesSchema } from '@/schemas/vehicule-check-result/result-image-coordinates.schema';
import {
  useGetCarProcessedById,
  useGenerateReportEngine,
  useGetNextDataReport,
} from '@/hooks/ai.hooks';
import {
  getColorByValue,
  initReportEngine,
  forceInitReport,
} from '@/utils/ia.utils';
import { useGetModerationEquipments } from './equipment.hooks';
import { useGetModerationDamages } from './damage.hooks';

interface ReportDataProps {
  id: string;
  showOnlyDetections?: boolean;
  showOnlyModerations?: boolean;
  equipmentClassId?: number;
}

const useReportData = ({
  id,
  showOnlyDetections = false,
  showOnlyModerations = false,
  equipmentClassId,
}: ReportDataProps) => {
  const {
    reportData,
    reportEngineData,
    setReportEngineData,
    sendLogs,
    setSendLogs,
    setDataAnnotation,
    setDataAnnotationDamage,
    setListElement,
    setListDamage,
    dataAnnotationDamage,
  } = useContext(EngineCorrectionContext);

  const { mutate: generateReport } = useGenerateReportEngine();

  const { data: carProcessed, isPending } = useGetCarProcessedById(id);

  const { data: carProcessedNext } = useGetNextDataReport(id);

  const { equipmentsSelectOptions } = useGetModerationEquipments();
  const { allDamagesSelectOptions } = useGetModerationDamages();

  const dataCar = carProcessed?.data as Processing;

  const generateReportData = () => {
    if (
      reportEngineData.create.length === 0 &&
      reportEngineData.edit.length === 0 &&
      reportEngineData.remove.length === 0
    )
      generateReport(forceInitReport(id));
    else generateReport(reportEngineData);
    setReportEngineData(initReportEngine(id));
  };

  useEffect(() => {}, [dataAnnotationDamage]);

  useEffect(() => {
    if (sendLogs) {
      // generateReport(reportEngineData);
      setReportEngineData(initReportEngine(id));
    }
    setSendLogs(false);
  }, [reportData]);

  useEffect(() => {
    if (dataCar) {
      const reportDataInit = initReportEngine(id);
      setReportEngineData(reportDataInit);

      let dataCarStore: TResultImageCoordinatesSchema[] = [];

      if (showOnlyDetections) {
        dataCarStore =
          dataCar.result.detectionsElements.filter(
            (element) => Number(element?.elNumber) === equipmentClassId
          ) || [];
      } else if (showOnlyModerations) {
        dataCarStore =
          dataCar.moderationsElements.filter(
            (element: TResultImageCoordinatesSchema) =>
              Number(element?.elNumber) === equipmentClassId
          ) || [];
      } else {
        // PREVIOUS VERSION
        // dataCarStore = [
        //   ...dataCar.result.detectionsElements,
        //   ...(dataCar.moderationsElements || []),
        // ];

        // Filter out detections that have been moderated
        const filteredDetections = dataCar.result.detectionsElements.filter(
          (element) => !element.moderated
        );

        dataCarStore = [
          ...filteredDetections,
          ...(dataCar.moderationsElements || []),
        ];
      }

      // PREVIOUS VERSION
      // const dataDamageStory: TResultImageCoordinatesSchema[] = [
      //   ...dataCar.result.detectionsDamagesDetected,
      //   ...(dataCar.moderationsDamages || []),
      // ];

      // Filter out damage detections that have been moderated
      const filteredDamageDetections =
        dataCar?.result?.detectionsDamagesDetected?.filter(
          (element) => !element?.moderated
        );

      const dataDamageStory = [
        ...filteredDamageDetections,
        ...(dataCar?.moderationsDamages || []),
      ];

      setDataAnnotation([...dataCarStore]);
      setDataAnnotationDamage([...dataDamageStory]);

      const newListElement =
        dataCarStore?.map((data) => ({
          elNumber: data?.elNumber,
          color: getColorByValue(
            data?.elNumber?.toString(),
            true,
            equipmentsSelectOptions,
            allDamagesSelectOptions
          ),
        })) || [];

      const newListDamage =
        dataDamageStory?.map((data: TResultImageCoordinatesSchema) => ({
          elNumber: data?.elNumber,
          color: getColorByValue(
            data?.elNumber?.toString(),
            false,
            equipmentsSelectOptions,
            allDamagesSelectOptions
          ),
        })) || [];

      setListElement(newListElement);
      setListDamage(newListDamage);
    }
  }, [dataCar, showOnlyDetections, showOnlyModerations, equipmentClassId]);

  useEffect(
    () => () => {
      setListElement([]);
      setListDamage([]);
      setDataAnnotation([]);
      setDataAnnotationDamage([]);
    },
    []
  );

  return {
    dataCar,
    isPending,
    carProcessedNext,
    generateReportData,
  };
};

export default useReportData;
